/**
 * Decode a JWT payload without verifying the signature.
 * The gateway already authenticated the token. This only reads claims for UI gating.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Parse an Identity `authorities` claim.
 * Supports a string array, a bracketed list, or a comma-separated string.
 */
export function parseAuthoritiesClaim(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((value) => String(value).trim()).filter(Boolean);
  }

  if (typeof raw !== "string") return [];

  const trimmed = raw.trim();
  if (!trimmed) return [];

  const inner =
    trimmed.startsWith("[") && trimmed.endsWith("]") ? trimmed.slice(1, -1) : trimmed;

  return inner
    .split(",")
    .map((part) => part.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);
}

export function getAuthoritiesFromAccessToken(token: string | null | undefined): string[] {
  if (!token) return [];
  const payload = decodeJwtPayload(token);
  if (!payload) return [];

  const fromAuthorities = parseAuthoritiesClaim(payload.authorities ?? payload.permissions);
  if (fromAuthorities.length) return fromAuthorities;

  if (Array.isArray(payload.scopes)) {
    return payload.scopes.map((value) => String(value).trim()).filter(Boolean);
  }

  return [];
}
